<template>
	<div id="simple-calendar-app" v-if="calendarReadPermission">
		<vs-popup
			:fullscreen="fullscreen"
			:title="popupTitle"
			:active.sync="popupActive"
			class="vs-popup--content-custom"
		>
			<event-form
				v-if="popupActive"
				:dataEvent="dataEvent"
				:day="selectedDay"
				@changePopupActiveEmit="changePopupActive"
				@fullscreenChangeEmit="fullscreenChange"
			/>
		</vs-popup>
		<!-- Avatar Row Info Resource -->
		<vx-card class="mb-2">
			<div class="vx-row">
				<div class="vx-col md:w-2/3 sm:w-1/1">
					<div class="flex flex-col md:flex-row items-start justify-content">
						<img :src="avatar" class="mr-8 rounded h-24 w-24" />

						<div>
							<p class="text-lg font-medium truncate g-subtitle">
								{{ (currentResourceFirstname + " " + currentResourceLastname) | capitalize }}
							</p>
							<p class="text-lg font-small truncate g-label--normal">
								<small>{{ currentResourceEmail }}</small>
							</p>

							<!-- Toggle comment of below buttons as one for actual flow & currently shown is only for demo -->
							<vs-button
								class="mr-4 mb-1 mt-2 g-label"
								icon-pack="feather"
								icon="icon-user"
								type="border"
								color="primary"
								@click.stop="toggleDataSidebar(true)"
								>{{ windowWidth >= 768 ? $t("Schedule.SelectResource") : "" }}</vs-button
							>
						</div>
					</div>
				</div>

				<div class="vx-col md:w-1/3 sm:w-1/1 md:mt-3 mt-base">
					<div class="flex flex-wrap items-end justify-end">
						<vs-button
							:title="$t('Schedule.Reassign')"
							icon-pack="feather"
							icon="icon-edit"
							color="primary"
							v-if="calendarView !== 'move' && simpleCalendarEvents.length > 0"
							@click="changeCalendarViewTypes('move')"
							>{{ windowWidth >= 1360 ? $t("Schedule.Reassign") : "" }}</vs-button
						>
						<vs-button
							:title="$t('Statistics.Statistics')"
							icon-pack="feather"
							icon="icon-pie-chart"
							class="ml-1"
							color="primary"
							v-if="calendarView !== 'move' && simpleCalendarEvents.length > 0"
							@click="getStatisticsEvents(rid)"
							>{{ windowWidth >= 1360 ? $t("Statistics.Statistics") : "" }}</vs-button
						>

						<vs-button
							v-if="calendarView === 'move'"
							:title="$t('Save')"
							icon-pack="feather"
							icon="icon-save"
							color="success"
							@click="openConfirmUpdate"
							:disabled="!selectedResourceReassign || selectedEvents.length === 0 || !reassignDate"
							>{{ windowWidth >= 1360 ? $t("Save") : "" }}</vs-button
						>
						<vs-button
							v-if="calendarView === 'move'"
							:title="$t('Cancel')"
							icon-pack="feather"
							icon="icon-x"
							color="danger"
							class="ml-1 mt-1 sm:mt-0"
							@click="cancelReassignEvents()"
							>{{ windowWidth >= 1360 ? $t("Cancel") : "" }}</vs-button
						>
					</div>
				</div>
			</div>
		</vx-card>

		<!-- Calendar Content -->

		<div class="vx-card no-scroll-content" style="height: 100vh">
			<calendar-view
				v-if="calendarView === 'month' || calendarView === 'week'"
				ref="calendar"
				:locale="calendarLocale"
				:displayPeriodUom="calendarView"
				:displayPeriodCount="1"
				:startingDayOfWeek="1"
				:periodChangedCallback="periodChangedCallback"
				:show-date="showDate"
				:events="simpleCalendarEvents"
				enableDragDrop
				:doEmitItemMouseEvents="true"
				:eventTop="windowWidth <= 400 ? '2rem' : '3rem'"
				eventBorderHeight="0px"
				eventContentHeight="1.65rem"
				class="theme-default"
				:enable-drag-drop="true"
				@click-date="openAddNewEvent"
				@click-event="openEditEvent"
				@drop-on-date="eventDragged"
				@drag-start="dragStartEvent"
				@item-mouseenter="mouseEnter"
				@item-mouseleave="mouseLeave"
			>
				<div slot="header" class="mb-4">
					<div class="vx-row no-gutter">
						<div class="vx-col w-1/3 items-center sm:flex hidden">
							<!-- Add new event button -->
							<vs-button
								:title="$t('Schedule.AddNewVisit')"
								icon-pack="feather"
								icon="icon-plus"
								@click="openAddNewEvent(new Date())"
								>{{ +windowWidth >= 1360 ? $t("Schedule.AddNewVisit") : "" }}</vs-button
							>
							<!-- Add new refres button -->
							<vs-button
								class="ml-4"
								type="border"
								icon-pack="feather"
								icon="icon-refresh-cw"
								@click="getEvents(rid)"
								:title="$t('Schedule.Refresh')"
								>{{ +windowWidth >= 1360 ? $t("Schedule.Refresh") : "" }}</vs-button
							>
						</div>

						<!-- Current Month -->
						<div class="vx-col sm:w-1/3 w-full sm:my-0 my-3 flex sm:justify-end justify-center order-last">
							<div class="flex items-center">
								<feather-icon
									:icon="$vs.rtl ? 'ChevronRightIcon' : 'ChevronLeftIcon'"
									@click="updateMonth(-1)"
									svgClasses="w-5 h-5 m-1"
									class="cursor-pointer bg-primary text-white rounded-full"
								/>

								<span class="mx-3 text-xl font-medium whitespace-no-wrap">{{ showDate | month }}</span>

								<feather-icon
									:icon="$vs.rtl ? 'ChevronLeftIcon' : 'ChevronRightIcon'"
									@click="updateMonth(1)"
									svgClasses="w-5 h-5 m-1"
									class="cursor-pointer bg-primary text-white rounded-full"
								/>
							</div>
						</div>

						<div class="vx-col sm:w-1/3 w-full flex justify-center">
							<template v-for="(view, index) in calendarViewTypes">
								<vs-button
									v-if="calendarView === view.val"
									:key="String(view.val) + 'filled'"
									type="filled"
									class="p-3 md:px-8 md:py-3"
									:class="{
										'border-l-0 rounded-l-none': index,
										'rounded-r-none': calendarViewTypes.length !== index + 1,
									}"
									@click="changeCalendarViewTypes(view.val)"
									>{{ view.label }}</vs-button
								>
								<vs-button
									v-else
									:key="String(view.val) + 'border'"
									type="border"
									class="p-3 md:px-8 md:py-3"
									:class="{
										'border-l-0 rounded-l-none': index,
										'rounded-r-none': calendarViewTypes.length !== index + 1,
									}"
									@click="changeCalendarViewTypes(view.val)"
									>{{ view.label }}</vs-button
								>
							</template>
						</div>
					</div>

					<div class="vx-row sm:flex hidden mt-4">
						<div class="vx-col w-full flex">
							<!-- Labels -->
							<div class="flex flex-wrap sm:justify-start justify-center">
								<div class="flex items-center mr-4 mb-2">
									<div
										class="h-3 w-3 inline-block rounded-full mr-2"
										:class="'bg-ondemand'"
										style="border: 1px solid #000000"
									></div>
									<span>On Demand</span>
								</div>
								<div
									v-for="(label, index) in calendarLabels"
									:key="index"
									class="flex items-center mr-4 mb-2"
								>
									<div
										class="h-3 w-3 inline-block rounded-full mr-2"
										:class="'bg-' + labelColor(label.id)"
									></div>
									<span>{{ labelColorText(label.id) | capitalize }}</span>
									<span v-if="label.id === 8" :title="spanTitle"
										><vs-switch color="#F00" v-model="showCanceledEvents"></vs-switch
									></span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</calendar-view>

			<div class="vx-row" v-if="calendarView === 'day'">
				<div class="vx-col sm:w-1/3 items-center sm:flex hidden ml-4 mt-5">
					<!-- Add new event button -->
					<vs-button icon-pack="feather" icon="icon-plus" @click="openAddNewEvent(new Date())">{{
						$t("Schedule.AddNewVisit")
					}}</vs-button>
				</div>
				<div class="vx-col sm:w-1/3 w-full flex justify-center mt-5">
					<template v-for="(view, index) in calendarViewTypes">
						<vs-button
							v-if="calendarView === view.val"
							:key="String(view.val) + 'filled'"
							type="filled"
							class="p-3 md:px-8 md:py-3"
							:class="{
								'border-l-0 rounded-l-none': index,
								'rounded-r-none': calendarViewTypes.length !== index + 1,
							}"
							@click="changeCalendarViewTypes(view.val)"
							>{{ view.label }}</vs-button
						>
						<vs-button
							v-else
							:key="String(view.val) + 'border'"
							type="border"
							class="p-3 md:px-8 md:py-3"
							:class="{
								'border-l-0 rounded-l-none': index,
								'rounded-r-none': calendarViewTypes.length !== index + 1,
							}"
							@click="changeCalendarViewTypes(view.val)"
							>{{ view.label }}</vs-button
						>
					</template>
				</div>
			</div>

			<calendar-add-event v-if="calendarView === 'day'" ref="CalendarAddEvent" />

			<calendar-by-day v-if="calendarView === 'move'" />
		</div>

		<calendar-sidebar
			ref="CalendarSidebar"
			:isSidebarActive="addNewDataSidebar"
			@closeSidebar="toggleDataSidebar"
		/>

		<vs-popup
			:title="$t('Roles.Permissions.NotPermissionsTitle')"
			:active.sync="popupActivePermissions"
			class="vs-popup--content-custom"
		>
			<not-permission size="small"></not-permission>
		</vs-popup>

		<vs-popup
			:title="$t('Statistics.Statistics')"
			:active.sync="popupActiveStatistics"
			class="vs-popup--content-custom"
		>
			<p>{{ $t("Statistics.VisitsStatistics") }}.</p>
			<div class="contact__info flex flex-col truncate w-5/6">
				<h5 class="font-semibold text-primary mt-2">
					{{ $t("RoutesLegends.TotalVisits") }}
				</h5>
				<h1 class="truncate">{{ simpleCalendarEvents.length }}</h1>
			</div>
			<calendar-estatistics :statistics="statistics" v-if="statistics.length > 0 && popupActiveStatistics" />
		</vs-popup>

		<EventPopupInformation
			:popupActiveEventContent="popupActiveEventContent"
			:event="dataEvent"
			@actions="handleActions"
		/>
	</div>
	<not-permission v-else />
</template>

<script>
import {CalendarView, CalendarViewHeader} from "vue-simple-calendar";
import {Validator} from "vee-validate";
require("vue-simple-calendar/static/css/default.css");
import Datepicker from "vuejs-datepicker";
import {en, es} from "vuejs-datepicker/src/locale";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import vSelect from "vue-select";

// Store
import moduleActivity from "@/store/activity/moduleActivity.js";
import moduleSchedule from "@/store/schedule/moduleSchedule.js";
import moduleVisitPoint from "@/store/visit-point/moduleVisitPoint.js";
import moduleResource from "@/store/resource/moduleResource.js";

import DateUtils from "@/helpers/DateUtils.js";
import Commons from "@/helpers/commons.js";
import {downloadFileFromFirebase} from "@/helpers/firebaseDB.js";
import {dict} from "./messageValidator.js";

import VisitPointList from "../visit-point/list/VisitPointList.vue";
import CalendarSidebar from "./CalendarSidebar";
import CalendarAddEvent from "./CalendarAddEvent";
import EventForm from "./EventForm.vue";
import NotPermission from "@/views/pages/NotPermission.vue";
import CalendarByDay from "./CalendarByDay.vue";
import CalendarEstatistics from "./CalendarEstatistics.vue";

// Assets
import userAvatar from "@/assets/images/portrait/small/user-avatar.png";
import cardImage from "@/assets/images/pages/search-result.jpg";
import EventPopupInformation from "./EventPopupInformation.vue";
import Event from "../../models/Event.js";

// register custom messages
Validator.localize("en", dict);

export default {
	components: {
		CalendarView,
		CalendarViewHeader,
		Datepicker,
		flatPickr,
		vSelect,
		VisitPointList,
		CalendarSidebar,
		CalendarAddEvent,
		EventForm,
		CalendarByDay,
		NotPermission,
		CalendarEstatistics,
		EventPopupInformation,
	},
	data() {
		return {
			selectedDay: null,
			dataEvent: new Event({}),
			showDate: new Date(),
			numberDaysInMont: 30,
			disabledFrom: false,
			updateMode: false,
			id: 0,
			address: "",
			city: "",
			startDate: "",
			endDate: "",
			labelLocal: "none",
			langHe: es,
			langEn: en,
			url: "",
			calendarView: "month",
			activePromptAddEvent: false,
			activePromptEditEvent: false,
			popupActive: false,
			fullscreen: true,
			init_hour: null,
			end_hour: null,
			configdateTimePicker: {
				enableTime: true,
				enableSeconds: true,
				noCalendar: true,
			},

			active: false,
			addNewDataSidebar: false,
			avatar: userAvatar, //require("@/assets/images/portrait/small/user-avatar.png"),
			// Repeat select
			repeat: "NONE",

			// config loading button
			backgroundLoading: "primary",
			backgroundLoadingDel: "danger",
			colorLoading: "#fff",
			//Config Visit Point List
			visitPointListConfig: {
				rowSelection: "single", // multiple | single
				activeCheckbox: false,
				columns: ["identification", "name", "address", "city"],
				tableOptions: false,
				showItemsPerPage: true,
				showSearch: true,
				showActions: false,
			},
			// Id list for default checks
			relatedIds: [],
			visitPointRelated: null,
			dataEventDragged: null,
			colors: {
				agendado: "#28C76F",
				reprogramado: "#006EFF",
				cancelado: "#FF0000",
				finalizado: "#19663b",
				en_proceso: "#FF9F43",
				ondemand: "#886ce4",
			},
			visitPointHasError: false,
			popupActivePermissions: false,
			popupActiveStatistics: false,
			statistics: [],
			showCanceledEvents: false,
			popupActiveEventContent: false,
			cardImage: cardImage,
		};
	},
	computed: {
		calendarLocale() {
			return this.$store.state.localeApp;
		},
		calendarViewTypes() {
			return [
				{
					label: this.$t("Schedule.Month"),
					val: "month",
				},
				{
					label: this.$t("Schedule.Week"),
					val: "week",
				},
				{
					label: this.$t("Schedule.Day"),
					val: "day",
				},
			];
		},
		dateInitComputed: {
			get() {
				return DateUtils.toDefaultDateTime(this.showDate, "YYYY-MM-DD");
			},
			set(val) {},
		},
		dateFinalComputed: {
			get() {
				const dateFinalMilisecods = this.addDaysToDate(this.dateInitComputed, this.numberDaysInMont);
				const dateFinal = DateUtils.toDefaultDateTime(new Date(dateFinalMilisecods), "YYYY-MM-DD");
				return dateFinal;
			},
			set(val) {},
		},
		popupTitle() {
			return this.updateMode ? this.$t("PopupScheduleTitleUpdate") : this.$t("PopupScheduleTitleCreate");
		},
		visitPointRelatedInfo() {
			return this.visitPointRelated !== null
				? this.visitPointRelated.address + " " + this.visitPointRelated.city
				: "";
		},
		currentResourceFirstname() {
			const currentResource = this.$store.getters["moduleSchedule/getCurrentResource"];
			if (currentResource.hasOwnProperty("name")) {
				return currentResource.name;
			} else {
				return "";
			}
		},
		currentResourceLastname() {
			const currentResource = this.$store.getters["moduleSchedule/getCurrentResource"];
			if (currentResource.hasOwnProperty("lastname")) {
				return currentResource.lastname;
			} else {
				return "";
			}
		},
		currentResourceEmail() {
			const currentResource = this.$store.getters["moduleSchedule/getCurrentResource"];
			if (currentResource.hasOwnProperty("email")) {
				return currentResource.email;
			} else {
				return "";
			}
		},
		rid() {
			return !this.$store.state.moduleSchedule ? 0 : this.$store.state.moduleSchedule.currentResourceId;
		},
		simpleCalendarEvents() {
			if (!this.$store.state.moduleSchedule.events) {
				return [];
			}
			const events = this.$store.state.moduleSchedule.events;
			return events.map((event) => {
				const date = new Date(event.date_prog);
				const parseDate = date.setDate(date.getDate() + 1);
				const isOnDemand = event.on_demand > 0 ? "🟣" : "";
				return {
					id: event.init_hour + event.id,
					title: `<div title="${event.visit_point_id}">
								<span style="padding:10px: width: 110%;">${isOnDemand} ${event.init_hour} ${event.activity_name}</span>
								<div id="${event.init_hour + event.id}" class="vx-card"
									style="position:relative;
										display: none;
										background-color:white;
										font-size: small;
										color: gray;
										z-index: 999999;
										width: 100%;
										height: auto;
										border-radius: 5px;
										padding: 8px;
										margin-top: 2px;">
										<p class="font-semibold mb-1">${this.$t("VisitPoint")}</p>
										<p class="truncate" > ${this.$t("Name")}: ${event.visit_point_name}</p>
										<p> ${this.$t("Identification")}: ${event.visit_point_identification}</p>

									</div>
						</div>`,
					startDate: parseDate,
					endDate: parseDate,
					url: "",
					classes: "event-" + this.labelColor(event.event_status_id),
					//style:"color:red"
					activity_id: event.activity_id,
				};
			});
		},
		calendarLabels() {
			return this.$store.state.moduleSchedule.eventSatusLabels.filter((e) => e.id !== 1);
		},
		spanTitle() {
			return this.$t("View") + " " + this.$t("Schedule.Canceled");
		},
		labelColorText() {
			return (label) => {
				let text = "";
				switch (label) {
					case 1:
						text = "NOT_ASSIGNED";
						break;
					case 2:
						text = this.$t("Schedule.Scheduled");
						break;
					case 3:
						text = this.$t("Schedule.ReProgramming");
						break;
					case 4:
						text = this.$t("Schedule.Delayed");
						break;
					case 5:
						text = this.$t("Schedule.OnTheWay");
						break;
					case 6:
						text = this.$t("Schedule.InProgress");
						break;
					case 7:
						text = this.$t("Schedule.Finished");
						break;
					case 8:
						text = this.$t("Schedule.Canceled");
						break;
					default:
						break;
				}
				return text.toLowerCase();
			};
		},
		labelColor() {
			return (label, ondemand = 0) => {
				let color = "";
				switch (label) {
					case 1:
						color = "NOT_ASSIGNED";
						break;
					case 2:
						color = "agendado";
						break;
					case 3:
						color = "reprogramado";
						break;
					case 4:
						color = "delayed";
						break;
					case 5:
						color = "on-the-way";
						break;
					case 6:
						color = "en_proceso";
						break;
					case 7:
						color = "finalizado";
						break;
					case 8:
						color = "cancelado";
						break;
					default:
						break;
				}
				return ondemand === 0 ? color : "ondemand";
			};
		},
		windowWidth() {
			return this.$store.state.windowWidth;
		},
		calendarReadPermission() {
			return this.$store.getters.checkPermission("CALENDAR_R");
		},
		calendarCreatePermission() {
			return this.$store.getters.checkPermission("CALENDAR_C");
		},
		calendarUpdatePermission() {
			return this.$store.getters.checkPermission("CALENDAR_U");
		},
		selectedEvents() {
			return this.$store.state.moduleSchedule.selectedEvents;
		},
		selectedResourceReassign() {
			return this.$store.getters["moduleResource/getSelectedResource"]();
		},
		reassignDate() {
			return this.$store.state.moduleSchedule.reassignDate;
		},
	},
	methods: {
		getFirstDayOfCurrentMonth() {
			const date = new Date();
			return new Date(date.getFullYear(), date.getMonth(), 1);
		},
		getLastDayOfCurrentMonth() {
			const date = new Date();
			return new Date(date.getFullYear(), date.getMonth() + 1, 0);
		},
		differentceInDaysBetweenDates(dateI, dateF) {
			//var dateI = new Date('2021-10-22 00:00:00')
			//var dateF = new Date('2021-10-25 00:00:00')
			const totalTime = dateF.getTime() - dateI.getTime(); // To calculate the no. of days between two dates
			const totalDays = totalTime / (1000 * 3600 * 24);
			return totalDays;
		},
		periodChangedCallback(val) {
			const current = new Date();
			const diff = this.differentceInDaysBetweenDates(this.showDate, current);
			// Si el cambio de calendario es a semana validamos las fechas para mostrar a partir del dia actual y no desde
			// el inicio del mes, actualizando los periodos si se cumplen los criterios de los calculos
			if (this.calendarView === "week") {
				if (this.showDate.getTime() <= current.getTime() && diff > 5) {
					this.showDate = this.$refs.calendar.getIncrementedPeriod(1);
				}
			}
		},
		fullscreenChange(val) {
			this.fullscreen = val;
		},
		changePopupActive({active, refres}) {
			this.popupActive = active;
			if (refres) {
				this.getEvents(this.rid);
			}
		},
		close() {
			this.$refs.CalendarSidebar.toggleDataSidebar(false);
		},
		// Funcion para agregar días a una fecha parseada desde un String, ya que cuendo se crea
		// dicha fecha siempre toma un día menos
		addDaysToDate(stringDate, dayNums) {
			// 1000 * 60 * 60 * 24 * 3
			const date = new Date(stringDate);
			return date.setDate(date.getDate() + dayNums);
		},
		async changeCalendarViewTypes(typeView) {
			await this.$store.dispatch("moduleSchedule/cleanEvents");
			this.calendarView = typeView;
			if (typeView === "month") {
				// actualizar fecha al primer dia del mes actual
				this.showDate = new Date(this.showDate.getFullYear(), this.showDate.getMonth(), 1);
				// Set number days current month
				this.numberDaysInMont = DateUtils.getNumberOfDaysInSeletedMonth(
					this.showDate.getMonth() + 1,
					this.showDate.getFullYear()
				);

				this.getEvents(this.rid);
			}
		},
		cancelReassignEvents(resourceId = this.rid) {
			this.calendarView = "month";
			this.getEvents(resourceId);
		},
		toggleDataSidebar(val = false) {
			this.active = !this.active;
			this.addNewDataSidebar = val;
		},
		openLoading() {
			this.$vs.loading();
		},
		closeLoading() {
			this.$vs.loading.close();
		},
		openLoadingButton(btnId, backgroundLoading = this.backgroundLoading) {
			this.$vs.loading({
				background: backgroundLoading,
				color: this.colorLoading,
				container: btnId,
				scale: 0.45,
			});
		},
		closeLoadingButton(btnId) {
			this.$vs.loading.close(btnId + " > .con-vs-loading");
		},
		generateOptionSelect(data, propLabel, propValue) {
			const newData = data.map((d) => {
				return {label: d[propLabel], value: d[propValue]};
			});
			newData.unshift({label: "Seleccione", value: 0});
			return newData;
		},
		getEvents(resource_id, dateInit = null, dateFinal = null) {
			const payload = {
				init_date: dateInit ? dateInit : this.dateInitComputed,
				end_date: dateFinal ? dateFinal : this.dateFinalComputed,
				resource_id: resource_id,
			};

			if (this.showCanceledEvents) {
				payload["event_status_id"] = [1, 2, 3, 4, 5, 6, 7, 8];
			}

			if (this.calendarView === "day") {
				const calendarDayDate = this.$refs.CalendarAddEvent.getCurrentDay();
				payload.init_date = DateUtils.toDefaultDateTime(calendarDayDate, "YYYY-MM-DD");
				payload.end_date = DateUtils.toDefaultDateTime(calendarDayDate, "YYYY-MM-DD");
			}

			this.openLoading();
			this.$store
				.dispatch("moduleSchedule/fetchEventsByResourceId", payload)
				.then((res) => {
					this.closeLoading();
					this.getResourcePhoto();
				})
				.catch((err) => {
					this.closeLoading();
					console.error(err);
				});
		},
		getStatisticsEvents(resource_id) {
			this.openLoading();
			const payload = {
				init_date: this.dateInitComputed,
				end_date: this.dateFinalComputed,
				resource_id: [resource_id],
				group_by: true,
			};
			this.statistics = [];
			this.$store
				.dispatch("moduleSchedule/statisticsEvent", payload)
				.then((res) => {
					this.statistics = res.data;
					this.popupActiveStatistics = true;
					this.closeLoading();
				})
				.catch((err) => {
					this.closeLoading();
					console.error(err);
				});
		},
		validateForm() {
			return !this.errors.any();
		},
		updateMonth(val) {
			this.showDate = this.$refs.calendar.getIncrementedPeriod(val);
			this.updateComputedDates();
			// get events for current dates
			this.getEvents(this.rid);
		},
		updateComputedDates() {
			// Calculate number of days for current month
			this.numberDaysInMont = DateUtils.getNumberOfDaysInSeletedMonth(
				this.showDate.getMonth() + 1,
				this.showDate.getFullYear()
			);
			const dateInit = DateUtils.toDefaultDateTime(this.showDate, "YYYY-MM-DD");
			const dateFinalMilisecods = this.addDaysToDate(dateInit, this.numberDaysInMont);
			const dateFinal = DateUtils.toDefaultDateTime(new Date(dateFinalMilisecods), "YYYY-MM-DD");
			// Update computeds properties
			this.dateInitComputed = dateInit;
			this.dateFinalComputed = dateFinal;
		},
		addNewEventDialog(date) {
			if (this.calendarCreatePermission) {
				this.startDate = date;
				this.endDate = date;
				this.selectedDay = date;
				this.dataEvent = {};
				this.popupActive = true;
			} else {
				this.popupActivePermissions = true;
			}
		},
		openAddNewEvent(date = new Date()) {
			const currentDate = new Date();
			currentDate.setHours(0, 0, 0, 0);
			if (date.getTime() < currentDate.getTime()) {
				this.$vs.notify({
					color: "warning",
					title: this.$t("Schedule.ValidData.DayLessThanCurrentDayTitle"),
					text: this.$t("Schedule.ValidData.DayLessThanCurrentDay"),
				});
				return;
			}
			if (this.rid > 0) {
				this.$router
					.push({
						name: `calendar-create-event`,
						params: {resource_id: this.rid, event_day: date},
					})
					.catch(() => {});
			} else {
				this.toggleDataSidebar(true);
			}
		},
		openEditEvent(event) {
			this.popupActiveEventContent = true;
			const foundEvent = this.$store.state.moduleSchedule.events.find((ele) => ele.calendarId === event.id);
			if (foundEvent) this.dataEvent = new Event(foundEvent);
			else this.dataEvent = new Event();
		},
		editEvent() {
			const currentVisitPoint = this.$store.getters["moduleVisitPoint/getCurrentVisitPoint"];
			const obj = {
				// Required fields
				event_id: this.id,
				resource_id: this.rid,
				date_prog: DateUtils.toDefaultDateTime(this.startDate, "YYYY-MM-DD"),
				init_hour: this.init_hour,
				end_hour: this.end_hour,
			};

			if (currentVisitPoint.id > 0) {
				// Add Optional fields
				obj.visit_point_id = currentVisitPoint.id;
				obj.cx = currentVisitPoint.cx;
				obj.cy = currentVisitPoint.cy;
				obj.city = currentVisitPoint.city;
				obj.address = currentVisitPoint.address;
			}

			this.$store
				.dispatch("moduleSchedule/editEvent", obj)
				.then((res) => {
					this.closeLoading();
					if (res.hasOwnProperty("error") && res.error.length > 0) {
						this.validateErrorMessages(res.error[0].message);
						return;
					}
					this.$vs.notify({
						color: "success",
						title: this.$t("TitleConfirmEventUpdate"),
						text: this.$t("TextConfirmEventUpdate"),
					});
				})
				.catch((err) => {
					this.closeLoading();
					this.showErrorAlert();
				});
		},
		removeEvent() {
			this.openLoading();
			const obj = {
				events: [{event_id: this.dataEvent.id, resource_id: this.rid}],
			};
			this.$store
				.dispatch("moduleSchedule/removeEvent", obj)
				.then((res) => {
					if (res.data.error) {
						this.showErrorAlert();
						return;
					}

					this.$vs.notify({
						color: "success",
						title: this.$t("TitleConfirmEventDelete"),
						text: this.$t("TextConfirmEventDelete"),
					});
				})
				.catch((err) => {
					this.showErrorAlert();
				})
				.finally(() => {
					this.closeLoading();
				});
		},
		showErrorAlert() {
			this.$vs.notify({
				color: "danger",
				title: this.$t("Schedule.ErrorProcess.Title"),
				text: this.$t("Schedule.ErrorProcess.Text"),
			});
		},
		async cleanEvents() {
			await this.$store.dispatch("moduleSchedule/cleanEvents");
		},
		eventDragged(event, date) {
			const e = this.$store.getters["moduleSchedule/getEventByCalendarId"](event.id);
			this.dataEventDragged = e;
			this.id = e.id;
			this.startDate = date;
			this.init_hour = e.init_hour;
			this.end_hour = e.end_hour;
			this.openLoading();
			this.editEvent();
		},
		stringTimeToDate(stringTime) {
			let newDate = new Date();
			if (typeof stringTime === "string") {
				const [hours, minutes, seconds] = stringTime.split(":");
				newDate = new Date(1995, 11, 17, parseInt(hours), parseInt(minutes), parseInt(seconds));
			}
			return newDate;
		},
		validatori(value, args) {
			return this.stringTimeToDate(value) > this.stringTimeToDate(this.init_hour);
		},
		fetch_resource_data(userId) {
			this.$store
				.dispatch("moduleResource/fetchResourceById", userId)
				.then((res) => {
					if (res.data) {
						this.$store.dispatch("moduleSchedule/selectedResource", res.data);
					}
				})
				.catch((err) => {
					if (err.response.status === 404) {
						this.$store.dispatch("moduleSchedule/selectedResource", {});
					}
					console.error(err);
				});
		},
		getResourcePhoto() {
			const currentResource = this.$store.getters["moduleSchedule/getCurrentResource"];
			const url = currentResource.img_url;
			if (!url) {
				this.avatar = userAvatar;
				return;
			}
			downloadFileFromFirebase(url)
				.then((res) => {
					this.avatar = res;
				})
				.catch((error) => {
					this.avatar = userAvatar;
				});
		},
		dragStartEvent(event) {},
		updateMultiEvents() {
			const selectedResource = this.selectedResourceReassign;
			if (!selectedResource) {
				this.$vs.notify({
					color: "danger",
					title: "Seleccione Recurso",
					text: "Por favor seleccione un recurso del listado para reasignar las visitas",
					time: 5000,
				});
				return;
			}
			const new_date_prog = this.reassignDate;
			const events = this.selectedEvents.map(({date_prog, id, resource_id}) => {
				return new_date_prog
					? {
							date_prog: new_date_prog,
							event_id: id,
							resource_id,
							new_resource_id: selectedResource.id,
					  }
					: {
							event_id: id,
							resource_id,
							new_resource_id: selectedResource.id,
					  };
			});
			this.openLoading();
			this.$store
				.dispatch("moduleSchedule/editEvent", events)
				.then((res) => {
					this.closeLoading();
					if (res.error.length > 0) {
						const eventErrors = res.error || [];
						this.$vs.notify({
							color: "danger",
							title: this.$t("Schedule.ErrorProcess.Title"),
							text: `${eventErrors.length} ${this.$t("Schedule.ReassignTextError")}`,
							time: 8000,
						});
						const currentEvents = [...this.$store.state.moduleSchedule.events];
						const filterErrorEvents = currentEvents
							// Agregar la propiedad messege a la data de los eventos para ver el mensaje de error
							.map((value, index) => {
								const eventError = eventErrors.find(({identifier}) => identifier === index);
								return {...value, reassignedError: eventError ? eventError.message : null};
							})
							// Filtrar la data principal con los eventos que no fueron reasignados
							.filter((value, index) => eventErrors.find(({identifier}) => identifier === index));
						this.$store.commit("moduleSchedule/SET_SCHEDULE_EVENTS", filterErrorEvents);
					} else {
						this.$vs.notify({
							color: "success",
							title: this.$t("TitleConfirmEventUpdate"),
							text: this.$t("TextConfirmEventUpdate"),
						});
						// Modificamos el recurso del calendario
						this.$store.dispatch("moduleSchedule/selectedResource", selectedResource);
						this.calendarView = "month";
						//this.cancelReassignEvents(selectedResource.id);
					}
				})
				.catch((err) => {
					this.closeLoading();
					this.showErrorAlert();
				});
		},
		openConfirmUpdate() {
			this.$vs.dialog({
				type: "confirm",
				color: "success",
				title: this.$t("Schedule.Reassign"),
				text: this.$t("Schedule.ReassignTextConfirm"),
				accept: this.updateMultiEvents,
				acceptText: this.$t("Accept"),
				cancelText: this.$t("Cancel"),
			});
		},
		validateErrorMessages(message) {
			let text = "";
			switch (message) {
				case "activity not available for the resource":
					text = this.$t("Schedule.ActivityNoAvailable");
					break;
				case "init_hour less than current time":
					text = this.$t("Schedule.ErrorProcess.InitHourError");
					this.init_hour_error = true;
					break;
				case "visit point is not related to resource":
					text = this.$t("Schedule.ErrorProcess.VisitpointError");
					break;
				case "resource has no activities":
					text = this.$t("Schedule.ErrorProcess.ResourceActivityError");
					break;
				default:
					text = this.$t("ErrorMessage");
					break;
			}
			this.$vs.notify({
				time: 6000,
				color: "danger",
				title: this.$t("ErrorTitle"),
				text,
			});
		},
		mouseEnter(item, event) {
			const activity = this.$store.getters["moduleActivity/getActivityById"](item.originalEvent.activity_id);
			event.target.title = activity ? activity.description : this.$t("VisitPoint");
			document.getElementById(item.id).style.display = "block";
		},
		mouseLeave(item, event) {
			document.getElementById(item.id).style.display = "none";
		},
		goToEditEvent() {
			if (this.dataEvent) {
				this.$store.commit("moduleSchedule/SELECT_EVENT", this.dataEvent);
				this.$router
					.push({
						name: `calendar-create-event`,
						params: {
							resource_id: this.rid,
							event_id: this.dataEvent.id,
							event_day: this.dataEvent.date_prog,
						},
					})
					.catch(() => {});
			}
		},
		goToFollowers() {
			if (this.dataEvent) {
				this.$store.commit("moduleSchedule/SELECT_EVENT", this.dataEvent);
				if (this.dataEvent.schedule_follwers_id && this.dataEvent.schedule_follwers_id > 0) {
					this.$router
						.push({
							name: `calendar-follower-update`,
							params: {event_id: this.dataEvent.id, follower_id: this.dataEvent.schedule_follwers_id[0]},
						})
						.catch(() => {});
				} else {
					this.$router
						.push({
							name: `calendar-follower-create`,
							params: {event_id: this.dataEvent.id},
						})
						.catch(() => {});
				}
			}
		},

		handleActions(action) {
			switch (action) {
				case "delete":
					this.removeEvent();
					break;
				case "edit":
					this.goToEditEvent();
					break;
				case "followers":
					this.goToFollowers();
					break;
				default:
					break;
			}

			this.popupActiveEventContent = false;
		},
	},
	filters: {
		replaceUnderscore(sentence) {
			return sentence.includes("_") ? sentence.replace("_", " ") : sentence;
		},
	},
	watch: {
		rid(resourceId) {
			this.toggleDataSidebar(false);
			this.cleanEvents();
			const dateInit = this.getFirstDayOfCurrentMonth();
			const dateEnd = this.getLastDayOfCurrentMonth();
			this.getEvents(
				resourceId,
				DateUtils.toDefaultDateTime(dateInit, "YYYY-MM-DD"),
				DateUtils.toDefaultDateTime(dateEnd, "YYYY-MM-DD")
			);
		},
		popupActive(val) {
			if (!val) {
				this.$store.dispatch("moduleVisitPoint/currentVisitPointSelected", {});
				this.$store.commit("moduleSchedule/SET_UNSAVED_EVENT", []);
			}
		},
		showCanceledEvents() {
			this.getEvents(this.rid);
		},
	},
	created() {
		if (!moduleActivity.isRegistered) {
			this.$store.registerModule("moduleActivity", moduleActivity);
			moduleActivity.isRegistered = true;
		}

		if (!moduleSchedule.isRegistered) {
			this.$store.registerModule("moduleSchedule", moduleSchedule);
			moduleSchedule.isRegistered = true;
		}

		// Register Module VisitPoint Module
		if (!moduleVisitPoint.isRegistered) {
			this.$store.registerModule("moduleVisitPoint", moduleVisitPoint);
			moduleVisitPoint.isRegistered = true;
		}

		if (!moduleResource.isRegistered) {
			this.$store.registerModule("moduleResource", moduleResource);
			moduleResource.isRegistered = true;
		}

		if (this.$store.state.moduleSchedule.eventSatusLabels.length <= 0) {
			this.$store
				.dispatch("moduleSchedule/fetchEventStatusLabel")
				.then((res) => {})
				.catch((err) => {
					console.error(err);
				});
		}
		if (this.$store.state.moduleActivity.activities.length <= 0) {
			this.$store
				.dispatch("moduleActivity/fetchActivityListItems")
				.then((res) => {})
				.catch((err) => {
					Commons.manageError.call(this, err);
					console.error(err);
				});
		}
	},
	beforeDestroy() {
		this.$store.unregisterModule("calendar");
		this.cleanEvents();
		this.$store.dispatch("moduleSchedule/selectedResource", {});
	},
	mounted() {
		this.cleanEvents();
		if (this.$route.params.id) {
			this.fetch_resource_data(+this.$route.params.id);
		} else {
			// actualizar fecha al primer dia del mes actual
			this.showDate = new Date(this.showDate.getFullYear(), this.showDate.getMonth(), 1);
			// Set number days current month
			this.numberDaysInMont = DateUtils.getNumberOfDaysInSeletedMonth(
				this.showDate.getMonth() + 1,
				this.showDate.getFullYear()
			);
			setTimeout(() => {
				this.toggleDataSidebar(true);
			}, 200);
		}
	},
	/**
	 * @description
	 * Se implementa guard para que cuando se a salir del componente se cierre el sidebar de los recursos primero
	 */
	beforeRouteLeave(to, from, next) {
		this.popupActiveEventContent = false;
		if (this.addNewDataSidebar) {
			this.toggleDataSidebar(false);
			setTimeout(() => {
				next();
			}, 500);
		} else {
			next();
		}
	},
};
</script>

<style lang="scss">
@import "../../assets/scss/vuexy/apps/simple-calendar.scss";

.vs-popup--content-custom {
	.vs-popup--content {
		height: 100%;
	}
	.btn-save {
		position: absolute;
		left: cal(100vw- 10%);
		bottom: 30px;
	}
	.btn-del {
		position: absolute;
		left: 200px;
		bottom: 30px;
	}
}
</style>

<style lang="scss" scoped>
/** custom content for map */
.map-custom-container {
	margin: 50px;
	height: 200px !important;
}
.custom-view-btn {
	margin-top: 60px;
}
.bg-agendado {
	background-color: #3aaaff !important;
}
.bg-reprogramado {
	background-color: #ffe15a !important;
}

.bg-cancelado {
	background-color: #ff0000 !important;
}
.bg-finalizado {
	background-color: #28c76f !important;
}
.bg-en_proceso {
	background-color: #113a60 !important;
}
.bg-delayed {
	background-color: #ff7070 !important;
}
.bg-on-the-way {
	background-color: #ffbe81 !important;
}

.bg-selected {
	background-color: #a0a0a0 !important;
}

.bg-ondemand {
	background-color: #886ce4 !important;
}
.tooltip {
	display: none;
	background-color: yellow;
	color: black;
}
</style>
